import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  "Brand Investment",
  "Wholesale Distribution",
  "Press & Media",
  "Private Atelier Bespoke",
];

const EMPTY = { name: "", email: "", organization: "", inquiry_type: TYPES[0], message: "" };

const inputCls =
  "w-full bg-white border border-ink/15 px-4 py-3 text-sm text-ink placeholder:text-mutedink/60 focus:outline-none focus:border-oxblood transition-colors duration-300";

const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink mb-2";

export const Inquiry = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry received", {
        description: `Reference ${data.reference}. We will reply within two business days.`,
      });
      setForm(EMPTY);
    } catch (err) {
      toast.error("Could not send your inquiry", {
        description: "Please check the fields and try again.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="inquiries"
      data-testid="inquiry-section"
      className="px-6 py-16 sm:py-20 border-t border-ink/10 bg-bone"
    >
      <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-10">
        <div>
          <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">Contact Us</h2>
          <p className="mt-5 max-w-md text-base font-light text-[#2A2A2A] leading-relaxed">
            For brand investment, wholesale, press, or private commissions, send us a message and
            we will get back to you.
          </p>
          <p
            data-testid="inquiry-email-meta"
            className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink"
          >
            bureau@haileyandchloe.holdings
          </p>
        </div>

        <form
          onSubmit={submit}
          data-testid="inquiry-form"
          className="bg-white border border-ink/10 p-6 sm:p-8 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="inquiry-name" className={labelCls}>
                Full Name *
              </label>
              <input
                id="inquiry-name"
                data-testid="inquiry-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Ava Sinclair"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="inquiry-email" className={labelCls}>
                Email Address *
              </label>
              <input
                id="inquiry-email"
                type="email"
                data-testid="inquiry-email-input"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="ava@maison.com"
                className={inputCls}
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="inquiry-organization" className={labelCls}>
                Organization
              </label>
              <input
                id="inquiry-organization"
                data-testid="inquiry-organization-input"
                value={form.organization}
                onChange={set("organization")}
                placeholder="Maison Sinclair Ltd."
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="inquiry-type" className={labelCls}>
                Inquiry Type *
              </label>
              <select
                id="inquiry-type"
                data-testid="inquiry-type-select"
                value={form.inquiry_type}
                onChange={set("inquiry_type")}
                className={inputCls}
              >
                {TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="inquiry-message" className={labelCls}>
              Message *
            </label>
            <textarea
              id="inquiry-message"
              data-testid="inquiry-message-input"
              required
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder="How can we help?"
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            type="submit"
            data-testid="inquiry-submit-btn"
            disabled={sending}
            className="inline-flex items-center gap-3 bg-oxblood text-parchment px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-oxbloodDark disabled:opacity-60 transition-colors duration-300"
          >
            {sending ? (
              <>
                Sending
                <Loader2 className="w-4 h-4 animate-spin" />
              </>
            ) : (
              <>
                Submit
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};
